import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

let apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
if (apiUrl && !apiUrl.startsWith("http://") && !apiUrl.startsWith("https://")) {
  apiUrl = `https://${apiUrl}`;
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = useCallback(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("isAuthenticated");
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const persistAuth = useCallback((userData, accessToken, refreshToken) => {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("isAuthenticated", "true");
    setUser(userData);
    setIsAuthenticated(true);
  }, []);

  // Try to restore session on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem("user");
      const accessToken = localStorage.getItem("accessToken");
      const refreshTokenVal = localStorage.getItem("refreshToken");

      if (storedUser && accessToken) {
        try {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        } catch (e) {
          // If token refresh fails, try refresh token
          if (refreshTokenVal) {
            try {
              const response = await axios.post(`${apiUrl}/auth/refresh`, {
                refreshToken: refreshTokenVal,
              });
              localStorage.setItem("accessToken", response.data.accessToken);
              localStorage.setItem("refreshToken", response.data.refreshToken);
              setUser(JSON.parse(storedUser));
              setIsAuthenticated(true);
            } catch (refreshError) {
              clearAuth();
            }
          } else {
            clearAuth();
          }
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, [clearAuth]);

  const login = async (email, password, orgSlug) => {
    const response = await axios.post(`${apiUrl}/auth/login`, {
      email,
      password,
      orgSlug,
    });
    if (response.data?.mfaRequired) {
      // Contraseña correcta, falta el segundo paso: nada se persiste todavía.
      return { mfaRequired: true, mfaToken: response.data.mfaToken };
    }
    const { user: userData, accessToken, refreshToken } = response.data;
    persistAuth(userData, accessToken, refreshToken);
    return userData;
  };

  const verifyMfa = async (mfaToken, code) => {
    const response = await axios.post(`${apiUrl}/auth/mfa/verify`, { mfaToken, code });
    const { user: userData, accessToken, refreshToken } = response.data;
    persistAuth(userData, accessToken, refreshToken);
    return userData;
  };

  const loginWithGoogle = async (credential, orgSlug) => {
    const response = await axios.post(`${apiUrl}/auth/google`, {
      credential,
      orgSlug,
    });
    if (response.data?.mfaRequired) {
      return { mfaRequired: true, mfaToken: response.data.mfaToken };
    }
    const { user: userData, accessToken, refreshToken } = response.data;
    persistAuth(userData, accessToken, refreshToken);
    return userData;
  };

  const register = async (email, password, name, lastName, orgSlug, code) => {
    const response = await axios.post(`${apiUrl}/auth/register`, {
      email,
      password,
      name,
      lastName,
      orgSlug,
      code,
    });
    const { user: userData, accessToken, refreshToken } = response.data;
    if (accessToken && refreshToken && userData) {
      persistAuth(userData, accessToken, refreshToken);
    }
    return response.data;
  };

  const logout = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      await axios.post(`${apiUrl}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (error) {
      // Logout is best-effort
    }
    clearAuth();
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        loginWithGoogle,
        verifyMfa,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
